import useDidUpdateEffect from 'hooks/useNoInitialEffect';
import { useCallback, useEffect, useRef, useState } from 'react';

export type EnrollmentViewTimer = {
    enableTimer: () => void;
    disableTimer: () => void;
};

/**
 * Controle de Tempo da Página de Inscrição
 * @param timeout Intervalo de tempo para inspecionar se houve interação na página.
 * @param register Função chamada para registrar o tempo de visualização.
 * @param ignoreFocusedWindow Função chamada para verificar se deve ou não ignorar a "Regra de Janela Ativa".
 * @param ignoreWindowActivity Função chamada para verificar se deve ou não ignorar a "Regra de Atividade na Janela de Inscrição".
 * @returns
 */
export function useEnrollmentViewTimer(
    timeout: number
    , register: (seconds: number) => void
    , ignoreFocusedWindow: () => boolean
    , ignoreWindowActivity: () => boolean
): EnrollmentViewTimer {

    const lastEvent = useRef<Date>(new Date());
    const seconds = useRef<number>(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [active, setActive] = useState<boolean>(true);

    const handleEvent = () => {
        lastEvent.current = new Date();
    };

    const handleActive = () => {
        setActive(true);
    };

    const handleInactive = () => {
        setActive(false);
    };

    const startEventsListener = () => {
        document.addEventListener("mousemove", handleEvent);
        document.addEventListener("keypress", handleEvent);
        document.addEventListener("scroll", handleEvent);
        document.addEventListener("touchend", handleEvent);
        document.addEventListener("click", handleEvent);
        window.addEventListener("focus", handleActive);
        window.addEventListener("blur", handleInactive);
    };

    const cleanupEvents = () => {
        document.removeEventListener("mousemove", handleEvent);
        document.removeEventListener("keypress", handleEvent);
        document.removeEventListener("scroll", handleEvent);
        document.removeEventListener("touchend", handleEvent);
        document.removeEventListener("click", handleEvent);
        window.removeEventListener("focus", handleActive);
        window.removeEventListener("blur", handleInactive);
    };

    const enableTimer = useCallback(() => {
        if (timerRef.current == null) {
            timerRef.current = setInterval(() => {
                if (seconds.current++ > timeout) {
                    //
                    seconds.current = 0;
                    //
                    const currDate = new Date();
                    const diff = Math.round((currDate.getTime() - lastEvent.current.getTime()) / 1000);
                    if (diff < timeout || ignoreWindowActivity()) register(timeout);
                }
            }, 1000);
        }
    }, []);

    const disableTimer = useCallback(() => {
        if (timerRef.current != null) {
            flushTimer();
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const flushTimer = useCallback(() => {
        const currDate = new Date();
        const diff = Math.round((currDate.getTime() - lastEvent.current.getTime()) / 1000);
        if ((diff < timeout || ignoreWindowActivity()) && seconds.current > 0) register(seconds.current);
    }, []);

    useEffect(() => {
        startEventsListener();

        return () => {
            disableTimer();
            cleanupEvents();
        };
    }, []);

    useDidUpdateEffect(() => {
        if (!ignoreFocusedWindow()) {
            if (!active) disableTimer();
            else enableTimer();
        }
    }, [active]);

    return { enableTimer, disableTimer };
}
